import { createApi, fetchBaseQuery, type FetchBaseQueryMeta } from "@reduxjs/toolkit/query/react";
import { StreamProcessor, streamManager } from "../../vendor/agentUiStreaming";
import { getPresentationApiUrl } from "./presentationTransport";
import { toPresentationChatMessages, type IPresentationChatMessage, type IPresentationSessionResource } from "./presentationChatMessages";

export type { IPresentationChatMessage, IPresentationChatItem, IPresentationSessionResource } from "./presentationChatMessages";

export interface IPresentationAgent {
  resource_id?: string;
  agent: { agent_id: string; default_chat_input?: string; internal_params?: { function_agents?: boolean }; [key: string]: unknown };
  [key: string]: unknown;
}
export interface IPresentationChatScope { projectRef: string; joid: string }
export interface IPresentationSessionArgs extends IPresentationChatScope { chatId: string }
export interface IPresentationSession { resource: IPresentationSessionResource; messages: IPresentationChatMessage[] }
interface IPresentationSessionQueryMeta extends FetchBaseQueryMeta {
  lastMessageIdAtRequestStart?: string;
  wasStreamingAtRequestStart: boolean;
}
export interface IPresentationSendMessageArgs extends IPresentationSessionArgs {
  agentId?: string;
  content: string;
  files?: File[];
  metadata?: { chatInputCtx?: Record<string, unknown>; chatInputArtifacts?: unknown[]; contextId?: string; contextType?: string };
  native_agent?: boolean;
  orchestrator_hints?: Record<string, unknown>;
  stream?: boolean;
}

const scopedUrl = ({ projectRef, joid }: IPresentationChatScope, path: string) =>
  `${getPresentationApiUrl(projectRef, path)}?joid=${encodeURIComponent(joid)}`;

/** Presentation alone uses the existing project-authorized transport. Its scope
 * is in query arguments, so the host store can safely cache multiple projects. */
export const presentationChatApi = createApi({
  reducerPath: "presentationChatApi",
  baseQuery: fetchBaseQuery({ credentials: "include" }),
  tagTypes: ["PresentationSession"],
  endpoints: (builder) => ({
    getAgents: builder.query<IPresentationAgent[], IPresentationChatScope>({
      query: (scope) => scopedUrl(scope, "/agents"),
    }),
    getSession: builder.query<IPresentationSession, IPresentationSessionArgs>({
      queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
        const messagesAtRequestStart = selectCachedSession(getState(), args)?.messages ?? [];
        const lastMessageIdAtRequestStart = messagesAtRequestStart[messagesAtRequestStart.length - 1]?.id;
        const wasStreamingAtRequestStart = messagesAtRequestStart.some((message) => message.isStreaming);
        const result = await baseQuery(scopedUrl(args, `/sessions/${encodeURIComponent(args.chatId)}`));
        if (result.error) return { error: result.error };
        const resource = result.data as IPresentationSessionResource;
        return {
          data: { resource, messages: toPresentationChatMessages(resource) },
          meta: result.meta ? { ...result.meta, lastMessageIdAtRequestStart, wasStreamingAtRequestStart } : undefined,
        };
      },
      // A refetch started before/during a turn can arrive after it completes.
      // Keep local changes made since that request began, even after streaming ends.
      merge: (current, incoming, { baseQueryMeta }) => {
        const snapshot = baseQueryMeta as IPresentationSessionQueryMeta | undefined;
        return current.messages.some((message) => message.isStreaming) ||
          (snapshot && (snapshot.wasStreamingAtRequestStart ||
            current.messages[current.messages.length - 1]?.id !== snapshot.lastMessageIdAtRequestStart))
          ? current : incoming;
      },
      providesTags: (_result, _error, { projectRef }) => [{ type: "PresentationSession", id: projectRef }],
    }),
    sendMessage: builder.mutation<IPresentationChatMessage, IPresentationSendMessageArgs>({
      queryFn: async (args, { dispatch, signal }) => {
        const { projectRef, joid, chatId, agentId, content, metadata, native_agent = false, orchestrator_hints, files } = args;
        const scope = { projectRef, joid, chatId };
        const turnId = crypto.randomUUID();
        const userId = `user-${turnId}`;
        const assistantId = `assistant-${turnId}`;
        const previews: string[] = [];
        let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
        const stop = () => { void streamManager.stopStream(chatId); };
        try {
          // Slide actions can send before the chat mounts; establish the same
          // transcript cache that the panel and page replay subscribe to.
          await dispatch(presentationChatApi.endpoints.getSession.initiate(scope, { subscribe: false })).unwrap();
          const attachments = files?.map((file, index) => {
            const previewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined;
            if (previewUrl) previews.push(previewUrl);
            return { id: `${userId}-${index}`, name: file.name, mimeType: file.type, size: file.size, previewUrl };
          });
          dispatch(presentationChatApi.util.updateQueryData("getSession", scope, (draft) => {
            draft.messages.push({ id: userId, sender: "user", chatItems: [{ type: "text", id: `${userId}-text`, message: content }], attachments });
            draft.messages.push({ id: assistantId, sender: "assistant", chatItems: [], isStreaming: true, agentId });
          }));
          const hasFiles = Boolean(files?.length);
          const payload = {
            session_id: chatId, chat_input: content, native_agent, stream: true,
            metadata: metadata || {}, chat_input_ctx: metadata?.chatInputCtx, chat_input_artifacts: metadata?.chatInputArtifacts,
            ...((orchestrator_hints || hasFiles) ? { orchestrator_hints: { ...orchestrator_hints, ...(hasFiles ? { multimodal_chat_files: true } : {}) } } : {}),
          };
          let body: string | FormData = JSON.stringify(payload);
          if (hasFiles) {
            body = new FormData();
            body.append("payload", JSON.stringify(payload));
            files?.forEach((file) => (body as FormData).append("file", file, file.name));
          }
          const response = await fetch(scopedUrl(scope, "/chat"), {
            method: "POST", credentials: "include", signal, body,
            headers: hasFiles ? undefined : { "Content-Type": "application/json" },
          });
          if (!response.ok) throw new Error(`API error: ${response.status} - ${response.statusText}`);
          if (!response.body) throw new Error("Response body is not available for streaming");
          reader = response.body.getReader();
          const processor = new StreamProcessor({
            onContentUpdate: (chatItems) => {
              dispatch(presentationChatApi.util.updateQueryData("getSession", scope, (draft) => {
                const message = draft.messages.find((item) => item.id === assistantId);
                if (!message) return;
                message.chatItems = chatItems.map((item) => ({ ...item, timestamp: new Date().toISOString() }));
                message.plan = processor.getPlan();
                message.metadata = processor.getMetadata();
              }));
            },
          });
          streamManager.registerStream(chatId, processor, reader);
          signal.addEventListener("abort", stop, { once: true });
          if (signal.aborted) stop();
          const decoder = new TextDecoder();
          while (true) {
            const chunk = await reader.read();
            if (chunk.done) break;
            processor.processChunk(decoder.decode(chunk.value, { stream: true }));
          }
          const final: IPresentationChatMessage = {
            id: assistantId, sender: "assistant",
            chatItems: processor.getChatItems().map((item) => ({ ...item, timestamp: item.timestamp || new Date().toISOString() })),
            plan: processor.getPlan(), metadata: processor.getMetadata(), agentId, isStreaming: false,
          };
          dispatch(presentationChatApi.util.updateQueryData("getSession", scope, (draft) => {
            const index = draft.messages.findIndex((message) => message.id === assistantId);
            if (index >= 0) draft.messages[index] = final;
          }));
          return { data: final };
        } catch (error) {
          // Remove only this failed turn. Undoing an array patch could discard a
          // newer turn or a transcript refresh that arrived in the meantime.
          dispatch(presentationChatApi.util.updateQueryData("getSession", scope, (draft) => {
            draft.messages = draft.messages.filter((message) => message.id !== userId && message.id !== assistantId);
          }));
          previews.forEach((url) => URL.revokeObjectURL(url));
          return { error: { status: "FETCH_ERROR", error: error instanceof Error ? error.message : "Failed to send message" } };
        } finally {
          signal.removeEventListener("abort", stop);
          if (reader) { reader.releaseLock(); streamManager.unregisterStream(chatId); }
        }
      },
    }),
  }),
});

const selectCachedSession = (state: unknown, args: IPresentationSessionArgs): IPresentationSession | undefined => {
  const select = presentationChatApi.endpoints.getSession.select(args);
  return select(state as Parameters<typeof select>[0]).data;
};

export const { useGetAgentsQuery, useGetSessionQuery, useSendMessageMutation } = presentationChatApi;
